import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import type { GiftProduct, GiftProductSavePayload, GiftProductState } from '@/api/gift-products'

interface GiftProductDialogProps {
	open: boolean
	product: GiftProduct | null
	isSaving: boolean
	onOpenChange: (open: boolean) => void
	onSubmit: (payload: GiftProductSavePayload) => void
}

export function GiftProductDialog({
	open,
	product,
	isSaving,
	onOpenChange,
	onSubmit,
}: GiftProductDialogProps) {
	const [name, setName] = useState('')
	const [stock, setStock] = useState('0')
	const [state, setState] = useState<GiftProductState>(0)

	useEffect(() => {
		if (!open) return
		setName(product?.name ?? '')
		setStock(String(product?.stock ?? 0))
		setState(product?.state ?? 0)
	}, [open, product])

	const handleSubmit = () => {
		const trimmedName = name.trim()
		if (!trimmedName) {
			toast.error('请输入名称')
			return
		}
		const parsedStock = Number.parseInt(stock, 10)
		if (!Number.isFinite(parsedStock) || parsedStock < 0) {
			toast.error('库存数量不能小于 0')
			return
		}
		onSubmit({
			name: trimmedName,
			...(product ? {} : { stock: parsedStock }),
			state,
			description: product?.description ?? '',
			price: product?.price ?? 0,
		})
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{product ? '编辑礼物' : '新增礼物'}</DialogTitle>
					<DialogDescription>维护礼物名称、库存数量和上下架状态</DialogDescription>
				</DialogHeader>
				<div className="space-y-4">
					<div className="space-y-2">
						<Label htmlFor="gift-product-name">名称</Label>
						<Input
							id="gift-product-name"
							value={name}
							onChange={(event) => setName(event.target.value)}
							maxLength={100}
						/>
					</div>
					{product ? (
						<p className="text-sm text-muted-foreground">库存请通过调整库存操作维护</p>
					) : (
						<div className="space-y-2">
							<Label htmlFor="gift-product-stock">库存数量</Label>
							<Input
								id="gift-product-stock"
								type="number"
								min={0}
								value={stock}
								onChange={(event) => setStock(event.target.value)}
							/>
						</div>
					)}
					<div className="space-y-2">
						<Label>状态</Label>
						<Select value={String(state)} onValueChange={(value) => setState(Number(value) as GiftProductState)}>
							<SelectTrigger aria-label="产品状态">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="0">上架</SelectItem>
								<SelectItem value="1">下架</SelectItem>
							</SelectContent>
						</Select>
					</div>
				</div>
				<DialogFooter>
					<Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
						取消
					</Button>
					<Button type="button" onClick={handleSubmit} disabled={isSaving}>
						保存
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
